import type {
  DocsBlock,
  DocsCalloutBlock,
  DocsChecklistBlock,
  DocsKeyValuesBlock,
  DocsLinkBlock,
  DocsParamTableBlock,
  DocsSnippetBlock,
  DocsSnippetVariant,
  DocsStepsBlock,
  DocsTableCell,
} from '@nadoutong/contracts';
import type { Locale } from '../../i18n/index.ts';
import { Table, TBody, Td, THead, Th, Tr } from '../../ui/index.ts';
import { isKnownDocsBlockType } from './document.ts';
import { docsResolvedLabel } from './labels.ts';
import type { DocsPlaceholderValues } from './placeholders.ts';
import { substituteDocsPlaceholders } from './placeholders.ts';
import {
  ActionLink,
  Checklist,
  DocsCallout,
  KeyValueList,
  NumberedStep,
  SnippetCard,
} from './primitives.tsx';

export interface DocsBlockRenderContext {
  readonly locale: Locale;
  readonly values: DocsPlaceholderValues;
  readonly copyLabel: string;
  readonly onCopy: (text: string) => void;
  readonly sectionId: string;
  readonly blockIndex: number;
  readonly axisSelection: Readonly<Record<string, string>>;
}

/**
 * Draw one block, or nothing. Unknown `type` is the second conservative
 * fallback: skip that block, keep the rest of the section.
 */
export function DocsBlockView({
  block,
  ctx,
}: {
  readonly block: DocsBlock;
  readonly ctx: DocsBlockRenderContext;
}): JSX.Element | null {
  if (!isKnownDocsBlockType(block.type)) return null;
  switch (block.type) {
    case 'callout':
      return <CalloutView block={block} ctx={ctx} />;
    case 'snippet':
      return <SnippetView block={block} ctx={ctx} />;
    case 'param_table':
      return <ParamTableView block={block} ctx={ctx} />;
    case 'steps':
      return <StepsView block={block} ctx={ctx} />;
    case 'checklist':
      return <ChecklistView block={block} ctx={ctx} />;
    case 'key_values':
      return <KeyValuesView block={block} ctx={ctx} />;
    case 'link':
      return <LinkView block={block} ctx={ctx} />;
    default:
      return null;
  }
}

function CalloutView({
  block,
  ctx,
}: {
  readonly block: DocsCalloutBlock;
  readonly ctx: DocsBlockRenderContext;
}): JSX.Element {
  const title =
    block.title === null ? null : docsResolvedLabel(block.title, ctx.locale, ctx.values);
  return (
    <DocsCallout tone={block.tone} title={title}>
      <div className="flex flex-col gap-2">
        {block.body.map((paragraph) => (
          <p key={paragraph.en} className="m-0">
            {docsResolvedLabel(paragraph, ctx.locale, ctx.values)}
          </p>
        ))}
      </div>
    </DocsCallout>
  );
}

function SnippetView({
  block,
  ctx,
}: {
  readonly block: DocsSnippetBlock;
  readonly ctx: DocsBlockRenderContext;
}): JSX.Element | null {
  const variant = selectSnippetVariant(block.variants, ctx.axisSelection);
  if (variant === null) return null;
  const code = substituteDocsPlaceholders(variant.template, ctx.values);
  const title =
    block.title === null ? null : docsResolvedLabel(block.title, ctx.locale, ctx.values);
  const caption =
    block.caption === null ? null : docsResolvedLabel(block.caption, ctx.locale, ctx.values);
  return (
    <SnippetCard
      title={title}
      caption={caption}
      code={code}
      copyLabel={ctx.copyLabel}
      onCopy={() => ctx.onCopy(code)}
      testId={`docs-snippet-${ctx.sectionId}-${String(ctx.blockIndex)}`}
    />
  );
}

/**
 * A variant with an empty `select` matches any tab state (the
 * single-variant case). Otherwise every axis in `select` must equal the
 * current selection. No match → skip, do not guess a template.
 */
export function selectSnippetVariant(
  variants: readonly DocsSnippetVariant[],
  selection: Readonly<Record<string, string>>,
): DocsSnippetVariant | null {
  for (const variant of variants) {
    if (variantMatches(variant.select, selection)) return variant;
  }
  return null;
}

function variantMatches(
  select: Readonly<Record<string, string>>,
  selection: Readonly<Record<string, string>>,
): boolean {
  for (const axis of Object.keys(select)) {
    const wanted = select[axis];
    if (wanted === undefined) continue;
    if (selection[axis] !== wanted) return false;
  }
  return true;
}

function ParamTableView({
  block,
  ctx,
}: {
  readonly block: DocsParamTableBlock;
  readonly ctx: DocsBlockRenderContext;
}): JSX.Element {
  const caption =
    block.caption === null ? null : docsResolvedLabel(block.caption, ctx.locale, ctx.values);
  return (
    <div>
      {caption !== null ? (
        <h3 className="m-0 mb-2 text-body font-medium text-ink">{caption}</h3>
      ) : null}
      <Table testId={`docs-param-table-${ctx.sectionId}-${String(ctx.blockIndex)}`}>
        <THead>
          <Tr>
            {block.columns.map((column) => (
              <Th key={column.key}>{docsResolvedLabel(column.label, ctx.locale, ctx.values)}</Th>
            ))}
          </Tr>
        </THead>
        <TBody>
          {block.rows.map((row) => (
            <Tr key={JSON.stringify(row.cells)}>
              {block.columns.map((column) => {
                const cell = row.cells[column.key];
                const header = docsResolvedLabel(column.label, ctx.locale, ctx.values);
                const rendered = renderTableCell(cell, ctx);
                return (
                  <Td key={column.key} label={header} stack mono={rendered.mono}>
                    {rendered.text}
                  </Td>
                );
              })}
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

function renderTableCell(
  cell: DocsTableCell | undefined,
  ctx: DocsBlockRenderContext,
): { readonly text: string; readonly mono: boolean } {
  if (cell === undefined || cell.kind === 'empty') {
    return { text: '—', mono: false };
  }
  if (cell.kind === 'code') {
    return { text: substituteDocsPlaceholders(cell.value, ctx.values), mono: true };
  }
  return { text: docsResolvedLabel(cell.value, ctx.locale, ctx.values), mono: false };
}

function StepsView({
  block,
  ctx,
}: {
  readonly block: DocsStepsBlock;
  readonly ctx: DocsBlockRenderContext;
}): JSX.Element {
  return (
    <ol className="m-0 grid list-none grid-cols-1 gap-3 p-0 md:grid-cols-3">
      {block.steps.map((step, index) => (
        <NumberedStep
          key={step.title.en}
          n={index + 1}
          title={docsResolvedLabel(step.title, ctx.locale, ctx.values)}
        >
          {step.body.map((paragraph) => (
            <span key={paragraph.en} className="block">
              {docsResolvedLabel(paragraph, ctx.locale, ctx.values)}
            </span>
          ))}
        </NumberedStep>
      ))}
    </ol>
  );
}

function ChecklistView({
  block,
  ctx,
}: {
  readonly block: DocsChecklistBlock;
  readonly ctx: DocsBlockRenderContext;
}): JSX.Element {
  const title =
    block.title === null ? null : docsResolvedLabel(block.title, ctx.locale, ctx.values);
  return (
    <Checklist
      title={title}
      items={block.items.map((item) => docsResolvedLabel(item, ctx.locale, ctx.values))}
    />
  );
}

function KeyValuesView({
  block,
  ctx,
}: {
  readonly block: DocsKeyValuesBlock;
  readonly ctx: DocsBlockRenderContext;
}): JSX.Element {
  const title =
    block.title === null ? null : docsResolvedLabel(block.title, ctx.locale, ctx.values);
  return (
    <KeyValueList
      title={title}
      rows={block.entries.map((entry) => {
        const rendered = renderTableCell(entry.value, ctx);
        return {
          label: docsResolvedLabel(entry.label, ctx.locale, ctx.values),
          value: rendered.text,
          mono: rendered.mono,
        };
      })}
    />
  );
}

function LinkView({
  block,
  ctx,
}: {
  readonly block: DocsLinkBlock;
  readonly ctx: DocsBlockRenderContext;
}): JSX.Element {
  return (
    <ActionLink
      href={substituteDocsPlaceholders(block.href, ctx.values)}
      label={docsResolvedLabel(block.label, ctx.locale, ctx.values)}
      external={block.external}
    />
  );
}
